import type { ConnectionQuality, TileState } from "@core/features/video";

/**
 * Given current inputs, determines what tile state an attendee should be in.
 *
 * Returns a concrete TileState. The caller is responsible for timer logic:
 * - When this returns 'avatar' and the attendee previously had video (hadVideo),
 *   the caller should start a grace timer before applying the transition.
 * - When this returns 'video' or 'frozen', the caller should cancel any pending
 *   grace timer.
 */
export function resolveAttendeeTileState(params: {
  sourcePresent: boolean;
  intentCameraOn: boolean | undefined;
  intentFresh: boolean;
  connectionQuality: ConnectionQuality;
  bandwidthPaused: boolean;
  hadVideo: boolean;
}): TileState {
  const {
    sourcePresent,
    intentCameraOn,
    intentFresh,
    connectionQuality,
    bandwidthPaused,
    // hadVideo is read by the caller to decide grace timer behavior,
    // but both paths return 'avatar' in this pure function (see note below).
    hadVideo: _hadVideo,
  } = params;

  // Source is present — show video (unless bandwidth-paused)
  if (sourcePresent) {
    return bandwidthPaused ? 'frozen' : 'video';
  }

  // Source is NOT present below this line.

  // Fresh intent says camera off → avatar immediately
  if (intentCameraOn === false && intentFresh) {
    return 'avatar';
  }

  // Fresh intent says camera on → network issue → frozen frame
  if (intentCameraOn === true && intentFresh) {
    return 'frozen';
  }

  // No usable intent below this line.

  // Connection degraded → stay frozen (don't show avatar during instability)
  if (connectionQuality !== 'good') {
    return 'frozen';
  }

  // Connection is good, no intent — return 'avatar'.
  // NOTE: Both hadVideo=true and hadVideo=false return 'avatar' here.
  // The distinction matters for the *caller*, not the pure function:
  // when hadVideo=true, the caller wraps this in a grace timer (frozen → avatar).
  // when hadVideo=false, the caller applies 'avatar' immediately.
  // The hadVideo parameter exists so the caller can read it without maintaining
  // a parallel lookup — but the pure function's return value is the same.
  return 'avatar';
}
